export default class SearchInput {
  constructor($el, query) {
    this.onQueryCommit = this.onQueryCommit.bind(this);
    this.onQuerySelect = this.onQuerySelect.bind(this);
    this.appendSpace = this.appendSpace.bind(this);
    this.onChange = this.onChange.bind(this);
    this.delayedOnChange = this.delayedOnChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.select = this.select.bind(this);
    this.focus = this.focus.bind(this);
    this.highlight = this.highlight.bind(this);
    this.$el = $el;
    this.query = query;
    this.pressedKeyCode = null;
    this.focusing = false;
  }

  bind() {
    this.query.on('commit', this.onQueryCommit);
    this.query.on('select', this.onQuerySelect);
    this.$el.on('click', this.appendSpace);
    this.$el.on('keyup click focus', this.onChange);
    this.$el.on('touchend', this.delayedOnChange);
    this.$el.on('keydown', this.onKeyDown);
    this.$el.on('blur', this.onBlur);
  }

  unbind() {
    this.query.off('commit', this.onQueryCommit);
    this.query.off('select', this.onQuerySelect);
    this.$el.off('click', this.appendSpace);
    this.$el.off('keyup click focus', this.onChange);
    this.$el.off('touchend', this.delayedOnChange);
    this.$el.off('keydown', this.onKeyDown);
    this.$el.off('blur', this.onBlur);
  }

  onQueryCommit() {
    if (this.$el.val() !== this.query.searchText) {
      const start = this.$el.prop('selectionStart');
      const end = this.$el.prop('selectionEnd');
      this.$el.val(this.query.searchText);
      this.select(start, end);
    }
  }

  onQuerySelect(...args) {
    const [start, end] = Array.from(args[0]);
    this.focus();
    this.select(start, end);
    if (start === 0) {
      this.$el.scrollLeft(0);
    }
  }

  appendSpace() {
    // Don't run for programmatic focuses
    if (this.focusing) {
      return;
    }
    const start = this.$el.prop('selectionStart');
    const end = this.$el.prop('selectionEnd');
    const lastToken = this.query.tokens[this.query.tokens.length - 1];
    // Append a space if the cursor is at the end of the text and there isn't already a space.
    // We don't fire a query change event here becuase the click event will also be picked up
    // by `onChange` which does just that.
    if (
      start === end &&
      end === this.$el.val().length &&
      lastToken &&
      !lastToken.isWhitespace()
    ) {
      this.$el.val(`${this.$el.val()}\u2001`);
      this.$el[0].setSelectionRange(start + 1, end + 1);
    }
  }

  onChange() {
    if (this.focusing) {
      return;
    }
    const start = this.$el.prop('selectionStart');
    const end = this.$el.prop('selectionEnd');
    this.query.update(this.$el.val(), start, end);
    this.pressedKeyCode = null;
    if (this.$el.val() !== this.query.searchText) {
      this.$el.val(this.query.searchText);
      this.select(start, end);
    }
  }

  delayedOnChange() {
    clearTimeout(this.delayedOnChangeTimeout);
    this.delayedOnChangeTimeout = setTimeout(this.onChange, 100);
  }

  onKeyDown(e) {
    this.pressedKeyCode = e.keyCode;
    // Tab Key
    if (e.keyCode === 9) {
      e.preventDefault();
      e.stopPropagation();
      this.highlightNext(e.shiftKey);
      // Delete Key
    } else if (e.keyCode === 8 && (e.altKey || e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      this.query.deleteTokenAtCursor(this.$el.prop('selectionEnd'));
    }
  }

  onBlur() {
    this.query.stopEditing();
  }

  select(start, end) {
    // In safari & edge, setting selection range will trigger focus
    // so we only set it here if already focused.
    if (this.$el.is(':focus')) {
      this.$el[0].setSelectionRange(start, end);
    }
  }

  focus() {
    this.focusing = true;
    this.$el.focus();
    this.focusing = false;
  }

  highlight(token, startDelta, endDelta) {
    if (!token) {
      return 0;
    }
    let start = token.startOffset + token.valueStartOffset();
    let end = token.endOffset + token.valueEndOffset();
    if (startDelta != null) {
      start += startDelta;
    }
    if (endDelta != null) {
      end += endDelta;
    }
    this.focus();
    this.select(start, end);
    return end;
  }

  highlightNext(reverse) {
    let nextToken;
    const token = this.query.selectedToken;
    // If current token is a placeholder, delete it wen tab is pressed
    if (token.isPlaceholder() && token.index === 0) {
      this.query.stopEditing();
      nextToken = this.query.tokens[reverse ? this.query.tokens.length - 1 : 0];
      // If last token on line is whitespace, get the one before that
      if (reverse && nextToken.isWhitespace()) {
        nextToken = this.query.tokens[this.query.tokens.length - 2];
      }
      // Otherwise, find the next or previous non-whitespace token
    } else {
      const direction = reverse ? -1 : 1;
      let i = token.index + direction;
      while ((nextToken = this.query.tokens[i])) {
        if (!nextToken.isWhitespace()) {
          break;
        }
        i += direction;
      }
    }
    // Highlight the next token if it exists. Otherwise, prepend a placeholder
    // to the search string and highlight it.
    if (nextToken) {
      if (nextToken) {
        return this.highlight(nextToken);
      }
    } else {
      this.query.stopEditing();
      this.query.prependOrSelect('...');
      return this.$el.scrollLeft(0);
    }
    return 0;
  }
}
